import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Button,
  Modal,
  TextField,
  Select,
  Checkbox,
  InlineStack,
  Text,
  Badge,
  Frame,
  Toast,
  EmptyState,
  Spinner,
  Box,
  BlockStack,
  Divider,
} from '@shopify/polaris';
import { 
  EditIcon, 
  DeleteIcon, 
  PlusIcon
} from '@shopify/polaris-icons';

async function parseApiError(response) {
  let message = `Request failed (${response.status})`;
  try {
    const text = await response.text();
    if (!text) return message;
    try {
      const data = JSON.parse(text);
      if (data && typeof data.error === 'string' && data.error.trim()) {
        return data.error.trim();
      }
      if (data && typeof data.message === 'string' && data.message.trim()) {
        return data.message.trim();
      }
    } catch {
      return text.trim() || message;
    }
  } catch {
    /* ignore */
  }
  return message;
}

const api = {
  get: async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(await parseApiError(response));
    }
    return response.json();
  },
  post: async (url, data) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response));
    }
    return response.json();
  },
  patch: async (url, data) => {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response));
    }
    return response.json();
  },
  delete: async (url) => {
    const response = await fetch(url, { method: 'DELETE' });
    if (!response.ok) {
      throw new Error(await parseApiError(response));
    }
    return response.json();
  },
};

// Email validation function for comma-separated emails
const validateEmails = (emailString) => {
  if (!emailString || emailString.trim() === '') return true; // Empty is valid for optional fields
  
  const emails = emailString.split(',').map(email => email.trim());
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  for (const email of emails) {
    if (!emailRegex.test(email)) {
      return false;
    }
  }
  return true;
};

const COUNTRY_OPTIONS = [
  { label: 'United States', value: 'United States' },
  { label: 'Canada', value: 'Canada' },
  { label: 'United Kingdom', value: 'United Kingdom' },
  { label: 'Australia', value: 'Australia' },
  { label: 'Germany', value: 'Germany' },
  { label: 'France', value: 'France' },
  { label: 'Italy', value: 'Italy' },
  { label: 'Spain', value: 'Spain' },
  { label: 'Netherlands', value: 'Netherlands' },
  { label: 'Belgium', value: 'Belgium' },
  { label: 'Switzerland', value: 'Switzerland' },
  { label: 'Austria', value: 'Austria' },
  { label: 'Sweden', value: 'Sweden' },
  { label: 'Norway', value: 'Norway' },
  { label: 'Denmark', value: 'Denmark' },
  { label: 'Finland', value: 'Finland' },
  { label: 'Ireland', value: 'Ireland' },
  { label: 'India', value: 'India' },
  { label: 'China', value: 'China' },
  { label: 'Japan', value: 'Japan' },
  { label: 'South Korea', value: 'South Korea' },
  { label: 'Singapore', value: 'Singapore' },
  { label: 'United Arab Emirates', value: 'United Arab Emirates' },
  { label: 'Saudi Arabia', value: 'Saudi Arabia' },
  { label: 'South Africa', value: 'South Africa' },
  { label: 'Brazil', value: 'Brazil' },
  { label: 'Mexico', value: 'Mexico' },
  { label: 'Argentina', value: 'Argentina' },
  { label: 'Other', value: 'Other' },
];

export default function SalespersonRoutingPage() {
  const [salespersons, setSalespersons] = useState([]);
  const [routingRules, setRoutingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('salesperson');
  const [editingItem, setEditingItem] = useState(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, hasNext: false, hasPrev: false });

  // Form state
  const [formData, setFormData] = useState({
    // Salesperson fields
    name: '',
    email: '',
    phone: '',
    is_active: true,
    // Country assignment fields
    assignedCountry: '',
    cc_email: '',
  });

  const fetchSalespersons = useCallback(() => {
    setLoading(true);
    api.get(`/api/salespersons?page=${page}&limit=${limit}`)
      .then(res => {
        setSalespersons(res.data || []);
        setPagination(res.pagination || { total: 0, totalPages: 1, hasNext: false, hasPrev: false });
      })
      .catch(() => {
        setToastMessage('Failed to fetch salespersons');
        setToastActive(true);
      })
      .finally(() => setLoading(false));
  }, [page, limit]);

  const fetchRoutingRules = useCallback(() => {
    api.get('/api/country-routing')
      .then(res => {
        setRoutingRules(res.data || []);
      })
      .catch(() => {
        setToastMessage('Failed to fetch routing rules');
        setToastActive(true);
      });
  }, []);

  useEffect(() => {
    fetchSalespersons();
    fetchRoutingRules();
  }, [fetchSalespersons, fetchRoutingRules]);

  // Reset to page 1 when limit changes
  useEffect(() => { setPage(1); }, [limit]);

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    
    if (type === 'salesperson') {
      if (item) {
        // Get assigned country for this salesperson
        const assignedRule = routingRules
          .find(rule => rule.salesperson_id === item.id && rule.is_active);
        
        setFormData({
          name: item.name,
          email: item.email,
          phone: item.phone || '',
          is_active: item.is_active,
          assignedCountry: assignedRule ? assignedRule.country : '',
          cc_email: assignedRule ? assignedRule.cc_email || '' : '',
        });
      } else {
        setFormData({
          name: '',
          email: '',
          phone: '',
          is_active: true,
          assignedCountry: '',
          cc_email: '',
        });
      }
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setModalType('salesperson');
    setFormData({
      name: '',
      email: '',
      phone: '',
      is_active: true,
      assignedCountry: '',
      cc_email: '',
    });
  };

  const saveItem = async () => {
    try {
      if (modalType === 'salesperson') {
        const countryTrim = (formData.assignedCountry || '').trim();
        console.log('hii')

        if (countryTrim) {
          const routingRes = await api.get('/api/country-routing');
          const rules = routingRes.data || [];
          const occupied = rules.find(
            (r) =>
              String(r.country || '').trim() === countryTrim &&
              r.is_active !== 0 &&
              r.is_active !== false &&
              String(r.salesperson_id) !== String(editingItem?.id ?? '')
          );
          if (occupied) {
            const who = occupied.salesperson_name || `salesperson #${occupied.salesperson_id}`;
            setToastMessage(
              `Country "${countryTrim}" is already assigned to ${who}. Remove that assignment first, then try again.`
            );
            setToastActive(true);
            return;
          }
        }

        const payload = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          is_active: formData.is_active,
          cc_email: formData.cc_email || '',
          assignedCountry: formData.assignedCountry || '',
        };

        if (editingItem) {
          await api.patch(`/api/salespersons/${editingItem.id}`, payload);
          setToastMessage('Salesperson updated successfully');
        } else {
          await api.post('/api/salespersons', payload);
          setToastMessage('Salesperson saved successfully');
        }

        fetchSalespersons();
        fetchRoutingRules();
        closeModal();
      }
      setToastActive(true);
    } catch (error) {
      const msg =
        error instanceof Error && error.message
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Something went wrong. Please try again.';
      setToastMessage(msg);
      setToastActive(true);
    }
  };

  const deleteItem = async (type, id) => {
    try {
      if (type === 'salesperson') {
        await api.delete(`/api/salespersons/${id}`);
        fetchSalespersons();
        setToastMessage('Salesperson deleted successfully');
      } else if (type === 'routing') {
        await api.delete(`/api/country-routing/${id}`);
        fetchRoutingRules();
        fetchSalespersons();
        setToastMessage('Routing rule deleted successfully');
      }
      setToastActive(true);
    } catch (error) {
      setToastMessage(error.message || 'Error deleting item');
      setToastActive(true);
    }
  };

  const toggleStatus = async (type, id, isActive) => {
    try {
      if (type === 'salesperson') {
        const sp = salespersons.find((s) => String(s.id) === String(id));
        if (!sp) {
          setToastMessage('Salesperson not found');
          setToastActive(true);
          return;
        }
        await api.patch(`/api/salespersons/${id}`, {
          name: sp.name,
          email: sp.email,
          phone: sp.phone || '',
          is_active: isActive,
        });
        fetchSalespersons();
        setToastMessage(`Salesperson ${isActive ? 'activated' : 'deactivated'}`);
      } else if (type === 'routing') {
        await api.patch(`/api/country-routing/${id}`, { is_active: isActive });
        fetchRoutingRules();
        setToastMessage(`Routing rule ${isActive ? 'activated' : 'deactivated'}`);
      }
      setToastActive(true);
    } catch (error) {
      setToastMessage('Error updating status');
      setToastActive(true);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isFormValid = formData.name && 
                    formData.email && 
                    validateEmails(formData.email) && 
                    (!formData.cc_email || validateEmails(formData.cc_email)) &&
                    !formData.email.includes(',');

  // Create rows showing salespersons with their assigned countries
  const salespersonsWithCountriesRows = salespersons.map(salesperson => {
    const assignedCountries = routingRules
      .filter(rule => rule.salesperson_id === salesperson.id && rule.is_active)
      .map(rule => rule.country);
    
    return [
      <Text variant="bodyMd" fontWeight="semibold">{salesperson.name}</Text>,
      <Text variant="bodyMd">{salesperson.email}</Text>,
      <Text variant="bodyMd">{salesperson.phone || '—'}</Text>,
      <Text variant="bodyMd">
        {assignedCountries.length > 0 
          ? assignedCountries.join(', ')
          : <Text variant="bodyMd" tone="subdued">No countries assigned</Text>
        }
      </Text>,
      <Badge status={salesperson.is_active ? 'success' : 'subdued'}>
        {salesperson.is_active ? 'Active' : 'Inactive'}
      </Badge>,
      <InlineStack gap="200">
        <Button size="slim" icon={EditIcon} onClick={() => openModal('salesperson', salesperson)}>Edit</Button>
        <Button 
          size="slim" 
          icon={DeleteIcon} 
          destructive
          onClick={() => deleteItem('salesperson', salesperson.id)}
        >
          Delete
        </Button>
      </InlineStack>,
    ];
  });

  return (
    <Frame>
      <Page
        title="Salesperson & Country Assignment"
        subtitle="Create salespersons and assign countries with CC emails in one place"
        primaryAction={{
          content: 'Add Salesperson & Assign Countries',
          icon: PlusIcon,
          onAction: () => openModal('salesperson'),
        }}
      >
        <Layout>
          <Layout.Section>
            <Card>
              <Text variant="headingMd" as="h2">Salespersons with Country Assignments</Text>
              <Box padding="400">
                {loading ? (
                  <InlineStack align="center"><Spinner size="large" /></InlineStack>
                ) : salespersons.length === 0 ? (
                  <EmptyState
                    heading="No salespersons configured"
                    action={{
                      content: 'Add Salesperson',
                      onAction: () => openModal('salesperson'),
                    }}
                  >
                    <p>Add salespersons and assign countries for RFQ email routing.</p>
                  </EmptyState>
                ) : (
                  <>
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                      headings={['Salesperson', 'Email', 'Phone', 'Assigned Countries', 'Status', 'Actions']}
                      rows={salespersonsWithCountriesRows}
                      hoverable
                    />
                    {/* Pagination Controls */}
                    <Box padding="400">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                        <Text variant="bodySm" tone="subdued">
                          Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} salesperson{pagination.total !== 1 ? 's' : ''}
                        </Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <button
                            onClick={() => setPage(p => p - 1)}
                            disabled={!pagination.hasPrev}
                            style={{
                              padding: '6px 14px',
                              border: '1px solid #c9cccf',
                              borderRadius: '6px',
                              background: pagination.hasPrev ? '#fff' : '#f6f6f7',
                              color: pagination.hasPrev ? '#202223' : '#8c9196',
                              cursor: pagination.hasPrev ? 'pointer' : 'not-allowed',
                              fontSize: '13px',
                              fontFamily: 'sans-serif',
                            }}
                          >
                            ← Previous
                          </button>
                          <Text variant="bodySm" tone="subdued">
                            Page {page} of {pagination.totalPages}
                          </Text>
                          <button
                            onClick={() => setPage(p => p + 1)}
                            disabled={!pagination.hasNext}
                            style={{
                              padding: '6px 14px',
                              border: '1px solid #c9cccf',
                              borderRadius: '6px',
                              background: pagination.hasNext ? '#fff' : '#f6f6f7',
                              color: pagination.hasNext ? '#202223' : '#8c9196',
                              cursor: pagination.hasNext ? 'pointer' : 'not-allowed',
                              fontSize: '13px',
                              fontFamily: 'sans-serif',
                            }}
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    </Box>
                  </>
                )}
              </Box>
            </Card>
          </Layout.Section>
        </Layout>

        {/* Add/Edit Modal */}
        <Modal
          open={modalOpen}
          onClose={closeModal}
          title={editingItem ? 'Edit Salesperson & Country Assignments' : 'Add Salesperson & Assign Countries'}
          primaryAction={{
            content: editingItem ? 'Update' : 'Create',
            onAction: saveItem,
            disabled: !isFormValid,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: closeModal,
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              <TextField
                label="Name"
                value={formData.name}
                onChange={(value) => handleFormChange('name', value)}
                placeholder="John Doe"
                autoComplete="name"
              />
              
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(value) => handleFormChange('email', value)}
                placeholder="john.doe@example.com"
                autoComplete="email"
                error={formData.email && (!validateEmails(formData.email) || formData.email.includes(',')) ? 'Please enter a valid single email address' : ''}
                helpText="Enter the primary email address for this salesperson"
              />
              
              <TextField
                label="Phone (Optional)"
                type="tel"
                value={formData.phone}
                onChange={(value) => handleFormChange('phone', value)}
                placeholder="+1 (555) 123-4567"
                autoComplete="tel"
              />
              
              <Divider />
              
              <Text variant="headingMd" as="h3">Country Assignment</Text>
              
              <Text variant="bodySm" tone="subdued">
                Each country can only be assigned to one salesperson. To move a country to someone else, clear or delete it from the current salesperson first.
              </Text>
              
              <Select
                label="Country"
                options={COUNTRY_OPTIONS}
                value={formData.assignedCountry}
                onChange={(value) => handleFormChange('assignedCountry', value)}
                placeholder="Select country"
              />
              
              <TextField
                label="CC Email (Optional)"
                type="email"
                value={formData.cc_email || ''}
                onChange={(value) => handleFormChange('cc_email', value)}
                placeholder="cc1@example.com, cc2@example.com"
                autoComplete="email"
                error={formData.cc_email && !validateEmails(formData.cc_email) ? 'Please enter valid email addresses (comma-separated)' : ''}
                helpText="Enter one or more CC email addresses separated by commas"
              />
              
              <Checkbox
                label="Active"
                checked={formData.is_active}
                onChange={(value) => handleFormChange('is_active', value)}
              />
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Toast Notification */}
        {toastActive && (
          <Toast
            content={toastMessage}
            onDismiss={() => setToastActive(false)}
          />
        )}
      </Page>
    </Frame>
  );
}
